import { Controller } from "@hotwired/stimulus"
import { lock, unlock } from "../helpers/body_scroll_lock"

export default class extends Controller {
  static targets = ["card", "counter", "hint", "viewLink", "recipeIdInput",
                    "dayPickerButton", "preview", "previewCard", "previewTitle"]
  static values = {
    index: { type: Number, default: 0 },
    paths: { type: Array, default: [] },
    recipeIds: { type: Array, default: [] },
    recipeNames: { type: Array, default: [] },
    exitUrl: { type: String, default: "" }
  }

  connect() {
    this.dragging = false
    this.dragDirection = null
    this.startX = 0
    this.startY = 0
    this.currentX = 0
    // Only lock body scroll when the swipe overlay is actually visible.
    // The picker modal renders this controller inside a `class="hidden"`
    // wrapper that view-toggle reveals on demand — locking on every connect
    // would freeze the page on routes that merely include the partial.
    if (!this.element.closest(".hidden")) {
      lock()
      this.scrollLocked = true
    }
    this.showCurrent()
  }

  disconnect() {
    if (this.scrollLocked) {
      unlock()
      this.scrollLocked = false
    }
  }

  pointerdown(event) {
    if (event.target?.closest("button, a, form")) return
    this.dragging = true
    this.dragDirection = null
    this.startX = event.clientX
    this.startY = event.clientY
    this.currentX = 0
    this.element.setPointerCapture?.(event.pointerId)
  }

  pointermove(event) {
    if (!this.dragging) return
    const dx = event.clientX - this.startX
    const dy = event.clientY - this.startY

    if (!this.dragDirection && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
      this.dragDirection = Math.abs(dx) > Math.abs(dy) ? "horizontal" : "vertical"
    }
    if (this.dragDirection !== "horizontal") return

    this.currentX = dx
    const card = this.cardTargets[this.indexValue]
    if (!card) return
    card.style.transform = `translateX(${dx}px) rotate(${dx * 0.04}deg)`
    card.style.transition = "none"
    card.style.opacity = `${1 - Math.abs(dx) / 500}`
  }

  pointerup() {
    if (!this.dragging) return
    this.dragging = false

    const card = this.cardTargets[this.indexValue]
    if (!card) return

    if (this.dragDirection === "horizontal" && Math.abs(this.currentX) > 60) {
      const dir = this.currentX > 0 ? 400 : -400
      card.style.transition = "transform 0.25s ease-out, opacity 0.25s"
      card.style.transform = `translateX(${dir}px) rotate(${dir * 0.04}deg)`
      card.style.opacity = "0"

      if (this.hasHintTarget) this.hintTarget.classList.add("hidden")

      setTimeout(() => {
        card.style.transform = ""
        card.style.opacity = ""
        card.style.transition = ""
        this.currentX > 0 ? this.prev() : this.next()
      }, 250)
    } else {
      card.style.transition = "transform 0.2s ease-out, opacity 0.2s"
      card.style.transform = ""
      card.style.opacity = ""
      setTimeout(() => { card.style.transition = "" }, 200)
    }
  }

  pointercancel() {
    if (!this.dragging) return
    this.dragging = false
    const card = this.cardTargets[this.indexValue]
    if (card) {
      card.style.transform = ""
      card.style.opacity = ""
      card.style.transition = ""
    }
  }

  keydown(e) {
    if (e.key === "ArrowLeft") { e.preventDefault(); this.prev() }
    else if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); this.next() }
    else if (e.key === "Escape") this.close()
  }

  next() {
    if (this.indexValue < this.cardTargets.length - 1) {
      this.indexValue++
      this.showCurrent()
    }
  }

  prev() {
    if (this.indexValue > 0) {
      this.indexValue--
      this.showCurrent()
    }
  }

  close() {
    if (this.scrollLocked) {
      unlock()
      this.scrollLocked = false
    }

    if (this.exitUrlValue) {
      window.Turbo.visit(this.exitUrlValue)
      return
    }

    this.element.remove()

    const viewInput = document.querySelector('[data-dashboard-filter-target="viewInput"]')
    if (viewInput) viewInput.value = "grid"

    const toggle = document.querySelector('[data-dashboard-filter-target="viewToggle"]')
    if (toggle) {
      toggle.querySelectorAll("button").forEach(btn => {
        const isGrid = btn.dataset.view === "grid"
        btn.classList.toggle("bg-warm-600", isGrid)
        btn.classList.toggle("text-white", isGrid)
        btn.classList.toggle("bg-white", !isGrid)
        btn.classList.toggle("dark:bg-gray-800", !isGrid)
        btn.classList.toggle("text-gray-400", !isGrid)
      })
    }
  }

  favorite(event) {
    event.preventDefault()
    const btn = event.currentTarget
    const url = btn.dataset.favoriteUrl
    const method = btn.dataset.favoriteMethod
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const svg = btn.querySelector("svg")
    const isFavorited = svg.classList.contains("fill-current")

    // Optimistic toggle
    if (isFavorited) {
      svg.classList.remove("fill-current", "text-warm-500")
      svg.classList.add("text-white")
      svg.setAttribute("fill", "none")
      btn.dataset.favoriteMethod = "post"
    } else {
      svg.classList.add("fill-current", "text-warm-500")
      svg.classList.remove("text-white")
      svg.setAttribute("fill", "currentColor")
      btn.dataset.favoriteMethod = "delete"
    }

    fetch(url, {
      method: method.toUpperCase(),
      headers: { "X-CSRF-Token": token, "Accept": "text/html" },
    })
  }

  showCurrent() {
    this.cardTargets.forEach((card, i) => {
      card.classList.toggle("hidden", i !== this.indexValue)
    })

    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${this.indexValue + 1} / ${this.cardTargets.length}`
    }

    if (this.hasViewLinkTarget && this.pathsValue[this.indexValue]) {
      this.viewLinkTarget.href = this.pathsValue[this.indexValue]
    }

    if (this.hasRecipeIdInputTarget && this.recipeIdsValue[this.indexValue]) {
      this.recipeIdInputTarget.value = this.recipeIdsValue[this.indexValue]
    }

    if (this.hasDayPickerButtonTarget) {
      const id = this.recipeIdsValue[this.indexValue]
      const name = this.recipeNamesValue[this.indexValue]
      if (id !== undefined)   this.dayPickerButtonTarget.dataset.dayPickerRecipeIdParam = id
      if (name !== undefined) this.dayPickerButtonTarget.dataset.dayPickerRecipeNameParam = name
    }
  }

  openPreview() {
    if (!this.hasPreviewTarget) return
    const name = this.recipeNamesValue[this.indexValue]
    if (this.hasPreviewTitleTarget && name) this.previewTitleTarget.textContent = name

    if (this.hasPreviewCardTarget) {
      this.previewCardTargets.forEach((card, i) => {
        card.classList.toggle("hidden", i !== this.indexValue)
      })
    }

    this.previewTarget.classList.remove("hidden")
    this.previewTarget.scrollTop = 0
  }

  closePreview() {
    if (!this.hasPreviewTarget) return
    this.previewTarget.classList.add("hidden")
  }
}
